/**
 * Helpers for DocumentsNotary factory methods
 */

import type { IBaseNotary } from "../../BaseNotaryFactory";
import type { DocumentNotary } from "le-coffre-resources/dist/Notary";
import type { QueryPayload } from "../../../../types";
import { prepareGetByUidQueryForRequest } from "@Front/Api/helpers/getByUidClientOptionsHelpers";
import type { IGetDocumentNotaryparams } from "../DocumentsNotary";
import { createSendToRecipientFormData } from "@Front/Api/LeCoffreApi/helpers/fileRequestPayloadHelpers";

export type SendError = {
	customerUid: string;
	fileName?: string;
	error: string;
};

export type PerRecipientEmailStatus = "sent" | "failed" | "skipped";

export type PerRecipientEmailOutcome = {
	customer_uid: string;
	document_uids: string[];
	email_status: PerRecipientEmailStatus;
	skip_reason?: "NO_CUSTOMER_EMAIL" | "NO_OFFICE_NAME";
	error?: string;
};

export type SendResult = {
	success: boolean;
	created: number;
	errors: number;
	emails_sent?: number;
	emails_failed?: number;
	emails_skipped?: number;
	email_outcomes?: PerRecipientEmailOutcome[];
	details: {
		createdDocuments?: unknown[];
		errors?: SendError[];
	};
};

/**
 * Create GET method for documents notary
 */
export function createGetMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (q: IGetDocumentNotaryparams): Promise<DocumentNotary[]> => {
		const url = new URL(baseUrl);
		baseNotary.appendJsonSearchParams(url, { q });
		try {
			return await baseNotary.getRequest<DocumentNotary[]>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create GET by UID method for documents notary
 */
export function createGetByUidMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string, q?: QueryPayload): Promise<DocumentNotary> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		const { requestQuery, suppressUserFacingErrorOnFailure } = prepareGetByUidQueryForRequest(q);
		if (requestQuery) baseNotary.appendJsonSearchParams(url, { q: requestQuery });
		try {
			return await baseNotary.getRequest<DocumentNotary>(url);
		} catch (err) {
			if (!suppressUserFacingErrorOnFailure) {
				baseNotary.onError(err);
			}
			return Promise.reject(err);
		}
	};
}

/**
 * Create POST method for documents notary
 */
export function createPostMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (body: FormData): Promise<DocumentNotary> => {
		const url = new URL(baseUrl);
		try {
			return await baseNotary.postRequestFormData<DocumentNotary>(url, body);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create DELETE method for documents notary
 */
export function createDeleteMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (uid: string): Promise<void> => {
		const url = new URL(baseUrl);
		url.pathname = url.pathname.concat(`/${uid}`);
		try {
			await baseNotary.deleteRequest(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create send method for documents notary to a single recipient
 */
export function createSendToRecipientMethod(baseNotary: IBaseNotary, baseUrl: string) {
	return async (recipientUid: string, folderUid: string, files: File[]): Promise<SendResult> => {
		const url = new URL(baseUrl.concat("/send"));

		const formData = createSendToRecipientFormData(recipientUid, folderUid, files);

		try {
			return await baseNotary.postRequestFormData<SendResult>(url, formData);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}
